import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useTelegram } from './TelegramProvider';

interface AuthState {
  /** UUID Supabase-пользователя или null, если ещё не залогинен. */
  userId: string | null;
  /** Telegram ID пользователя для отображения. */
  telegramId: number | null;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthState>({
  userId: null,
  telegramId: null,
  isLoading: true,
  error: null,
});

/**
 * Авторизует пользователя в Supabase через Edge Function `tg-auth`,
 * которая валидирует Telegram initData и возвращает JWT-сессию.
 *
 * Если приложение открыто вне Telegram — авторизация пропускается
 * (полезно для локальной разработки UI).
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const { webApp, isReady } = useTelegram();
  const [state, setState] = useState<AuthState>({
    userId: null,
    telegramId: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    if (!isReady) return;
    let cancelled = false;

    const run = async (): Promise<void> => {
      try {
        if (!webApp || !webApp.initData) {
          if (!cancelled) {
            setState({ userId: null, telegramId: null, isLoading: false, error: null });
          }
          return;
        }

        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tg-auth`;
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ initData: webApp.initData }),
        });

        if (!res.ok) throw new Error(`tg-auth ${res.status}: ${await res.text()}`);
        const json = (await res.json()) as { access_token: string; refresh_token: string };

        const { data, error } = await supabase.auth.setSession({
          access_token: json.access_token,
          refresh_token: json.refresh_token,
        });
        if (error) throw error;

        if (!cancelled) {
          setState({
            userId: data.user?.id ?? null,
            telegramId: webApp.initDataUnsafe.user?.id ?? null,
            isLoading: false,
            error: null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            userId: null,
            telegramId: webApp?.initDataUnsafe.user?.id ?? null,
            isLoading: false,
            error: err instanceof Error ? err.message : 'Auth failed',
          });
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [isReady, webApp]);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

/** Хук для получения текущей сессии. */
export function useAuth(): AuthState {
  return useContext(AuthContext);
}
